/**
 * Klaviyo footer newsletter — keeps theme markup, submits via Client Subscription API.
 */
class DevKlaviyoNewsletter extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number | undefined} */
  #successMessageTimeoutId;

  /** @type {number} */
  #successMessageHideDelayMs = 8000;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.querySelector('form')?.addEventListener('submit', this.#onSubmit, { signal });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#clearSuccessMessageTimeout();
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();

    const emailInput = /** @type {HTMLInputElement | null} */ (this.querySelector('[data-ref="email-input"]'));
    const submitButton = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-ref="submit-button"]'));
    const messageElement = this.querySelector('[data-ref="notification-message"]');

    if (!emailInput || !submitButton || !messageElement) return;

    const email = emailInput.value.trim();
    const companyId = this.dataset.klaviyoCompanyId;
    const listId = this.dataset.klaviyoListId;

    if (!companyId || !listId) {
      this.#showMessage(
        emailInput,
        messageElement,
        this.dataset.msgError || 'There was an error. Please try again.',
        'error'
      );
      return;
    }

    if (!this.#validateEmail(email)) {
      emailInput.setAttribute('aria-invalid', 'true');
      this.#showMessage(
        emailInput,
        messageElement,
        this.dataset.msgInvalidEmail || 'Please enter a valid email address.',
        'error'
      );
      return;
    }

    emailInput.setAttribute('aria-invalid', 'false');
    this.#clearMessage(messageElement);

    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');

    try {
      const response = await this.#subscribe(email, companyId, listId);

      if (response.ok) {
        this.#showMessage(
          emailInput,
          messageElement,
          this.dataset.msgSuccess || 'Thanks for subscribing!',
          'success'
        );
        emailInput.value = '';
        this.#setSubscribedState(submitButton);

        if (typeof window._klaviyoNewsletterForm === 'function') {
          window._klaviyoNewsletterForm();
        }
      } else {
        let errorDetail = this.dataset.msgError || 'There was an error. Please try again.';

        try {
          const responseData = await response.json();
          const apiDetail = responseData.errors?.[0]?.detail;
          const errorTemplate = this.dataset.msgErrorDetail;

          if (apiDetail && errorTemplate) {
            errorDetail = errorTemplate.replace('{{ detail }}', apiDetail);
          } else if (apiDetail) {
            errorDetail = apiDetail;
          }
        } catch {
          // Keep generic error message when response body is not JSON.
        }

        this.#showMessage(emailInput, messageElement, errorDetail, 'error');
        submitButton.disabled = false;
        submitButton.removeAttribute('aria-busy');
      }
    } catch (error) {
      console.error('Klaviyo newsletter subscription failed:', error);
      this.#showMessage(
        emailInput,
        messageElement,
        this.dataset.msgError || 'There was an error. Please try again.',
        'error'
      );
      submitButton.disabled = false;
      submitButton.removeAttribute('aria-busy');
    }
  };

  /**
   * @param {string} email
   * @returns {boolean}
   */
  #validateEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.toLowerCase());
  }

  /**
   * @param {HTMLElement} messageElement
   */
  #clearMessage(messageElement) {
    this.#clearSuccessMessageTimeout();
    messageElement.hidden = true;
    messageElement.textContent = '';
    messageElement.replaceChildren();
    messageElement.classList.remove('email-signup__message--success', 'email-signup__message--error');
  }

  #clearSuccessMessageTimeout() {
    if (this.#successMessageTimeoutId !== undefined) {
      window.clearTimeout(this.#successMessageTimeoutId);
      this.#successMessageTimeoutId = undefined;
    }
  }

  /**
   * @param {HTMLButtonElement} submitButton
   */
  #setSubscribedState(submitButton) {
    submitButton.disabled = true;
    submitButton.setAttribute('aria-disabled', 'true');
    submitButton.removeAttribute('aria-busy');

    const subscribedLabel = this.dataset.msgSubscribed || 'Subscribed';

    if (submitButton.classList.contains('email-signup__button--text')) {
      submitButton.textContent = subscribedLabel;
    } else {
      submitButton.setAttribute('aria-label', subscribedLabel);
    }
  }

  /**
   * @param {'success' | 'error'} type
   * @returns {DocumentFragment | null}
   */
  #getMessageTemplate(type) {
    const selector =
      type === 'success' ? '[data-ref="message-success-template"]' : '[data-ref="message-error-template"]';
    const template = this.querySelector(selector);

    if (!(template instanceof HTMLTemplateElement)) {
      return null;
    }

    return template.content.cloneNode(true);
  }

  /**
   * @param {HTMLInputElement} emailInput
   * @param {HTMLElement} messageElement
   * @param {string} text
   * @param {'success' | 'error'} type
   */
  #showMessage(emailInput, messageElement, text, type) {
    messageElement.replaceChildren();

    const templateContent = this.#getMessageTemplate(type);

    if (templateContent) {
      const textElement = templateContent.querySelector('.email-signup__message-text');

      if (textElement) {
        textElement.textContent = text;
      }

      messageElement.append(templateContent);
    } else {
      messageElement.textContent = text;
    }

    messageElement.hidden = false;
    messageElement.classList.remove('email-signup__message--success', 'email-signup__message--error');
    messageElement.classList.add(
      type === 'success' ? 'email-signup__message--success' : 'email-signup__message--error'
    );

    const messageId = messageElement.id;
    if (messageId) {
      emailInput.setAttribute('aria-describedby', messageId);
      emailInput.setAttribute('aria-invalid', type === 'error' ? 'true' : 'false');
    }

    requestAnimationFrame(() => {
      messageElement.focus();
      messageElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });

    if (type === 'success') {
      this.#clearSuccessMessageTimeout();
      this.#successMessageTimeoutId = window.setTimeout(() => {
        this.#clearMessage(messageElement);
        emailInput.removeAttribute('aria-describedby');
        emailInput.setAttribute('aria-invalid', 'false');
      }, this.#successMessageHideDelayMs);
    }
  }

  /**
   * @returns {Record<string, string>}
   */
  #getProfileContext() {
    /** @type {Record<string, string>} */
    const properties = {};

    const market = this.dataset.shopifyMarket?.trim();
    const marketId = this.dataset.shopifyMarketId?.trim();
    const language = this.dataset.shopifyLanguage?.trim();
    const country = this.dataset.shopifyCountry?.trim();

    if (market) properties.shopify_market = market;
    if (marketId) properties.shopify_market_id = marketId;
    if (language) properties.shopify_language = language;
    if (country) properties.shopify_country = country;

    return properties;
  }

  /**
   * @returns {Record<string, unknown>}
   */
  #buildProfileAttributes(email) {
    /** @type {Record<string, unknown>} */
    const attributes = { email };

    const locale = this.dataset.shopifyLocale?.trim();
    if (locale) {
      attributes.locale = locale;
    }

    const countryName = this.dataset.shopifyCountryName?.trim();
    if (countryName) {
      attributes.location = { country: countryName };
    }

    const properties = this.#getProfileContext();
    if (Object.keys(properties).length > 0) {
      attributes.properties = properties;
    }

    return attributes;
  }

  /**
   * @param {string} email
   * @param {string} companyId
   * @param {string} listId
   * @returns {Promise<Response>}
   */
  async #subscribe(email, companyId, listId) {
    const customSource = this.dataset.klaviyoCustomSource || 'Footer newsletter';

    const payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: customSource,
          profile: {
            data: {
              type: 'profile',
              attributes: this.#buildProfileAttributes(email),
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    };

    return fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-07-15',
      },
      body: JSON.stringify(payload),
    });
  }
}

if (!customElements.get('dev-klaviyo-newsletter')) {
  customElements.define('dev-klaviyo-newsletter', DevKlaviyoNewsletter);
}
